import { gql } from "@apollo/client";

export const RAW_UNIT_QUERY = gql`
	query GetRawUnits($params: RawUnitQueryParams) {
		rawUnits(params: $params) {
			one {
				id
				propertyType {
					type
				}
				relationship
				customer {
					email
					name
					phone
				}
			}
		}
	}
`;

export const GET_POST_PROPERTY_QUERY = gql`
	query GetRawUnitListing($params: RawUnitQueryParams) {
		rawUnits(params: $params) {
			getPostProperty {
				# Basic identifiers
				id
				identifier
				status {
					shortlisted
				}
				groupId

				# Step 1: Profile fields
				name
				phoneNumber
				email
				relationship
				propertyType {
					type
					subType
					houseType
				}
				isCommunity

				# Step 2: Address fields
				projectName
				location {
					locality
				}
				address
				flatNumber
				blockName

				# Step 3: Basic Property Details fields
				price
				possessionStatus
				timeFrameToSell
				constructedOn
				carpetArea
				subArea
				plotArea
				undividedShare
				totalFloors
				floorNo
				totalBasements
				basements
				numUnits
				bedrooms
				bathrooms
				balconies
				coveredParking
				numParking
				facing
				plotFacing
				specialAttributes

				# Step 4: Additional Property Details fields
				registered
				isRectangular
				isCornerPlot
				plotSides
				approachRoadWidth
				flooring
				furnishing
				waterSource
				amenities
				notes

				# Step 5: Project/Community Details fields
				numEntrances
				totalFloors
				approvals

				# Step 6: Upload fields
				images {
					name
					tags
					size
				}
				documents {
					from
					isAuthorityVerified
					name
				}

				# Step 7: Fee Confirmation fields
				isConfirmationDone

				# Step 8: Slot Confirmation fields
				# flatNumber
				# blockName
				isSelfOccupied
				keyholderName
				keyholderMobile
				availabilitySlots
			}
		}
	}
`;

export const RAW_UNITS_QUERY = gql`
	query GetRawUnits($params: RawUnitQueryParams) {
		rawUnits(params: $params) {
			list {
				id
				identifier
				name
				description
				price
				location {
					locality
				}
				status {
					shortlisted
				}
				images {
					original
					name
					space
					tags
				}
				propertyType {
					type
					gated
					project
					villa
					villaType
					single
					penthouse
					villament
					level
				}
				spaces {
					bedrooms {
						number
					}
					bathrooms {
						number
					}
				}
				builtUpArea
				plotArea
				processedAt
			}
		}
	}
`;

export const SELLER_UNITS_QUERY = gql`
	query GetSellerUnits($params: SellerQueryParams) {
		seller(params: $params) {
			getSellerUnits {
				sellerUnit {
					id
					unit {
						id
						name
						identifier
					}
					processDetails {
						process
						stage
						status
						updatedAt
					}
				}
				sup {
					_id
					address
					appCoverImage
					amenities
					balconies
					bathrooms
					bedroom
					bedrooms
					builtUpAreas
					constructedOns
					coordinates
					cover
					currentRate
					facings
					floors
					gardens
					gps {
						lat
						lng
					}
					greatPriced
					halls
					identifier
					images {
						unitId
						projectId
						id
						name
						files {
							cover
							name
							tags
						}
						urlPrefix
						path
						original
						thumbnail
						space
						description
						tags
					}
					incompleteProcesses
					isCommunity
					lastCallAt
					lastRemarks
					levels
					locality
					lpd {
						stage
						status
						updatedAt
						process
						updatedBy
					}
					mediaList {
						name
						space
						type
						url
						tags
					}
					nonNegotiable
					numOfUnits
					ocStatus
					openSpaceArea
					optimization {
						process
						stage
						status
						updatedAt
					}
					owners
					parkings
					penthouse
					plotAreas
					plotFacings
					possessionStatuses
					price
					priceNum
					priceNums
					prices
					projectId
					projectName
					propertyAgeInMonths
					propertyTypes
					saleStatus
					sellerProjectId
					servantRooms
					totalBlocks
					totalFloors
					totalUnits
					type
					unitId
					unitNames
					urgent
					villament
					waterSource {
						borewell
						supply
						tanker
					}
					manual
					status
					cancellation {
						flag
					}
				}
			}
		}
	}
`;

const SELLER_UNITS_AND_PROJECTS_QUERY_COMMON_FIELDS = `
	_id
	additionalFeatures
	address
	amenities
	appCoverImage
	approvals
	bankApprovals
	balconies
	bathrooms
	bedrooms
	blocks {
		id
		name
	}
	builtUpAreas
	constructedOns
	groupId
	constructedOnsFromProject
	documents
	cover
	facings
	floors
	gardens
	greatPriced
	halls
	identifier
	isCommunity
	kitchens
	landParcel
	locality
	location {
		gps {
			coordinates
		}
	}
	mediaList {
		name
		space
		type
		url
		tags
	}
	monthlyMaintenanceCosts
	numOfUnits
	owners
	parkings
	plotAreas
	plotFacings
	possessionStatuses
	prices
	projectId
	projectName
	propertyTypes
	rentalValues
	servantRooms
	slug
	# spaces {
	# 	id
	# 	name
	# 	level
	# 	height
	# 	length
	# 	width
	# 	area
	# 	covered
	# 	view
	# 	flooring
	# 	furnishing
	# 	interiors
	# 	additionalFeatures
	# 	attachedTo
	# }
	spaceSummary {
		furnishing {
			type
			items {
				name
				space
			}
		}
		interiors {
			name
			space
		}
		flooring {
			name
			space
		}
		additionalFeatures
	}
	subUnits {
		unitId
		rentalValue
		propertyType
		facing
		bedrooms
		bathrooms
		halls
		kitchens
		floorplan
		floor
		images {
			unitId
			space
			name
			original
		}
	}
	totalBasements
	totalBlocks
	totalFloors
	totalUnits
	type
	utilities
	waterSource {
		supply
		borewell
		tanker
	}
	writeUpList
	saleStatus
	propertyTypeObj {
		gated
		project
		villa
		villaType
		single
		penthouse
		villament
		level
		type
	}
`;

export const SELLER_UNITS_AND_PROJECTS_QUERY = gql`
	query SellerUnitsAndProjects($params: SellerUnitsAndProjectsParams) {
		sellerUnitsAndProjects(params: $params) {
			details {
				${SELLER_UNITS_AND_PROJECTS_QUERY_COMMON_FIELDS}
				configIds
				loan {
					bank
					status
				}
				configurations {
					configuration {
						_id
						name
						displayName
						spaces {
							bedrooms {
								number
								details
							}
							bathrooms {
								number
								details
							}
							balconies {
								number
								details
							}
							halls {
								number
								details
							}
							kitchens {
								number
								details
							}
							utilities {
								number
								details
							}
						}
						facing
						floor
						floorplans
						uds
						additionalFeatures
						spaceArray
					}
					generalAvailability {
						isAvailable
					}
					exactAvailabilityIds
				}
			}
		}
	}
`;

export const SELLER_UNITS_AND_PROJECTS_GET_OPTIMIZATION_DETAILS_QUERY = gql`
	query SellerUnitsAndProjects($params: SellerUnitsAndProjectsParams) {
		sellerUnitsAndProjects(params: $params) {
			getOptimizationDetails {
				${SELLER_UNITS_AND_PROJECTS_QUERY_COMMON_FIELDS}
				unitId
				sellerUnitId
				lpd {
					stage
					status
					updatedAt
					process
					updatedBy
				}
				currentRate
				loan {
					bank
					loanRate
					outstanding
					outstandingAt
					status
				}
				negotiableUpto
				nonNegotiable
				optimization {
					process
					stage
					status
					updatedAt
				}
				constructionPrice
				constructionRate
				hasOpenInadequacyRequest
			}
		}
	}
`;

export const SELLER_PROJECT_SUMMARY_QUERY = gql`
	query SellerProjectSummary($visitQueryParams: VisitQueryParams) {
		visit(params: $visitQueryParams) {
			getSellerProjectSummary {
				_id
				priceSheetData {
					_id
					name
					base {
						value
					}
					increments {
						common {
							for
							value
						}
						unitSpecific {
							for
							value
						}
					}
					applicable {
						from
						configurations {
							id
							name
							basePrice
							builtUpArea
							exactAvailability {
								_id
								unit {
									name
									id
								}
							}
							generalAvailability {
								_id
								numOfUnits
								facings
								floors
								isAvailable
								blocks {
									name
									id
								}
							}
							configWiseSummary
							floorplans
							propertyType {
								type
							}
							carpetArea
							plotArea
							bhk
							facing
							bathrooms
						}
					}
					priceSheetFiles
					priceType
					construction {
						rate
						value
					}
				}
				marketing {
					status {
						active
						at
						by
						reason
					}
				}
				totalPricings {
					spus
					summary
				}
			}
		}
	}
`;

const list = `
	_id
	address
	amenities
	balconies
	bathrooms
	bedroom
	builtUpAreas
	constructedOns
	coordinates
	cover
	currentRate
	facings
	floors
	gardens
	gps {
		lat
		lng
	}
	greatPriced
	halls
	identifier
	images {
		unitId
		projectId
		id
		name
	}
	lastRemarks
	levels
	locality
	lpd {
		stage
		status
		updatedAt
		process
		updatedBy
	}
	mediaList {
		name
		space
		type
		url
		tags
	}
	nonNegotiable
	numOfUnits
	ocStatus
	openSpaceArea
	optimization {
		process
		stage
		status
		updatedAt
	}
	owners
	parkings
	penthouse
	plotAreas
	plotFacings
	possessionStatuses
	price
	priceNum
	priceNums
	projectId
	projectName
	propertyAgeInMonths
	propertyTypes
	saleStatus
	sellerProjectId
	servantRooms
	totalBlocks
	totalFloors
	totalUnits
	type
	unitId
	unitNames
	urgent
	villament
	waterSource {
		borewell
		supply
		tanker
	}
	manual
	status
	filterCounts {
		total
		matching
		matches {
			filter
			match
			deviation
			notes
		}
	}
	_searchMeta {
		count {
			total
		}
	}
	_score
	_scoreDetails {
		value
		description
		details {
			value
			description
			details {
				value
				description
				details {
					value
					description
					details {
						value
						description
					}
				}
			}
		}
	}
`;

export const GET_SELLER_UNITS_AND_PROJECTS_LIST = gql`query SellerUnitsAndProjects($params: SellerUnitsAndProjectsParams) {
	sellerUnitsAndProjects(params: $params) {
		list {
			${list}
		}
	}
}`;

export const GET_ACTIVE_SELLER_UNITS = gql`
	query GetActiveSellerUnits($params: SellerQueryParams) {
		seller(params: $params) {
			getSellerUnits {
				unit {
					name
					id
				}
			}
		}
	}
`;

export const REMOVE_RAW_UNIT_QUERY = gql`
	query Remove($params: RawUnitQueryParams) {
		rawUnits(params: $params) {
			remove {
				success
				message
			}
		}
	}
`;

export const GET_SELLER_B_LO_DATA_QUERY = gql`
	query Query($params: SellerQueryParams) {
		seller(params: $params) {
			getSellerGroup {
				id
				identifier
				name
				type
				createdAt
				entityType
				companyName
				gstNo
				officeAddress
				relationship
				projects {
					id
					name
					noOfFlatsForSale
					propertyTypes
					totalFlats
				}
			}
		}
	}
`;

export const REMOVE_SELLER_GROUP_QUERY = gql`
	query RemoveSellerGroup($params: SellerQueryParams) {
		seller(params: $params) {
			removeSellerGroup {
				success
				message
			}
		}
	}
`;

export const GET_RBI_PRICE_INDEX_QUERY = gql`
	query GetRBIPriceIndex($params: SellerQueryParams) {
		seller(params: $params) {
			getRBIPriceIndex {
				data {
					quarter
					index
				}
				latestQuarter
				latestIndex
			}
		}
	}
`;
